import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

const ProfessionalDetailsSection = ({ user, onEditPrimaryMarkets, onEditSpecializedBusinesses, onEditPracticeAreas }) => {
  const intl = useIntl();

  // Helper function to format display text: remove underscores and capitalize each word
  const formatDisplayText = (text) => {
    if (!text) return '';

    return text
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const renderList = (items, emptyText) => {
    if (!items || items.length === 0) {
      return <p className="text-succession-text-muted italic">{emptyText}</p>;
    }
    return (
      <div className="flex flex-wrap gap-2">
        {items.map((item, index) => (
          <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-succession-bg-primary border border-succession-border text-succession-text-primary">
            {formatDisplayText(item)}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
      <h3 className="text-xl font-semibold text-succession-text-primary mb-6">{intl.formatMessage({ id: 'succession.profile_edit.sections.professional_details.title' })}</h3>

      <div className="space-y-6">
        {/* Primary Markets */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-succession-text-secondary">{intl.formatMessage({ id: 'succession.profile_edit.sections.professional_details.primary_markets' })}</h4>
            <button
              onClick={onEditPrimaryMarkets}
              className="inline-flex items-center px-3 py-1.5 border border-succession-border rounded-lg text-sm text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {intl.formatMessage({ id: 'succession.profile_edit.common.edit' })}
            </button>
          </div>
          {renderList(user.primary_markets, intl.formatMessage({ id: 'succession.profile_edit.sections.professional_details.no_primary_markets' }))}
        </div>

        {/* Specialized Businesses */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-succession-text-secondary">{intl.formatMessage({ id: 'succession.profile_edit.sections.professional_details.specialized_businesses' })}</h4>
            <button
              onClick={onEditSpecializedBusinesses}
              className="inline-flex items-center px-3 py-1.5 border border-succession-border rounded-lg text-sm text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {intl.formatMessage({ id: 'succession.profile_edit.common.edit' })}
            </button>
          </div>
          {renderList(user.specialized_businesses, intl.formatMessage({ id: 'succession.profile_edit.sections.professional_details.no_specialized_businesses' }))}
        </div>

        {/* Practice Areas */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-succession-text-secondary">{intl.formatMessage({ id: 'succession.profile_edit.sections.professional_details.practice_areas' })}</h4>
            <button
              onClick={onEditPracticeAreas}
              className="inline-flex items-center px-3 py-1.5 border border-succession-border rounded-lg text-sm text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {intl.formatMessage({ id: 'succession.profile_edit.common.edit' })}
            </button>
          </div>
          {renderList(user.practice_areas, intl.formatMessage({ id: 'succession.profile_edit.sections.professional_details.no_practice_areas' }))}
        </div>
      </div>
    </div>
  );
};

ProfessionalDetailsSection.propTypes = {
  user: PropTypes.object.isRequired,
  onEditPrimaryMarkets: PropTypes.func.isRequired,
  onEditSpecializedBusinesses: PropTypes.func.isRequired,
  onEditPracticeAreas: PropTypes.func.isRequired,
};

export default ProfessionalDetailsSection;
